/**
 * OFICIALES — acreditar la mesa y el cuerpo arbitral.
 *
 * Dos cards que son los dos actos de la acreditación:
 *
 *  1. **Cuerpo arbitral** (`officials_set`) — lo que va al acta como caja
 *     APPROVAL. Se edita la lista completa y se registra de una: el evento
 *     reemplaza al anterior (último `officials_set` gana), así que corregir un
 *     nombre es volver a guardar, nunca editar el log.
 *  2. **Credenciales QR+PIN** (ADR-019) — una por persona presente. El server
 *     devuelve el token y el PIN UNA sola vez, y esta pantalla es el único
 *     lugar donde existen en claro: el QR se DIBUJA (`components/Qr.tsx`,
 *     sobre `qrcode-generator`) para escanearlo con la tablet, el link queda
 *     visible para copiar y el PIN se dicta al titular. Las credenciales que
 *     emitió ESTA laptop pueden volver a mostrar su QR desde la lista.
 *
 * El token emitido se guarda además en el navegador DE LA MESA
 * (`saveIssuedToken`): es lo que después permite que el titular firme el acta
 * acá mismo, canjeando su credencial y tecleando su PIN (ver `ActaTab`).
 */
import { useState } from 'react';
import type { MatchRole, OfficialRef, TeamSide } from '@scout/core';
import { useT } from '../../i18n/use-t';
import type { MessageKey } from '../../i18n/index';
import { Card, Empty, ErrorBox } from '../../components/States';
import { Qr } from '../../components/Qr';
import { errorMessage } from '../../hooks';
import type { IssuedCredentialResult, MatchCredential } from '../../lib/api-client';
import { benchCredentialUrl } from '../../lib/router';
import { defaultKeyValueStore } from '../../lib/settings';
import { freshLastSeq, roleKey, type MesaTabProps } from './MesaPage';
import {
  CREDENTIAL_ROLES,
  MESA_OFFICIAL_ROLES,
  loadIssuedTokens,
  mesaActor,
  officialsSetEvent,
  saveIssuedToken,
} from './mesa';

/** Copia de `DEFAULT_ORG_ID` (`@scout/core`): el tenant único del modo LAN. */
const DEFAULT_ORG = 'default';

/** Base del link del QR: el portal mismo, sin la parte del hash. */
function portalBaseUrl(): string {
  const g = globalThis as { location?: { origin: string; pathname: string } };
  if (!g.location) return '';
  return `${g.location.origin}${g.location.pathname}`.replace(/\/+$/, '');
}

async function copyToClipboard(text: string): Promise<boolean> {
  const g = globalThis as { navigator?: { clipboard?: { writeText(t: string): Promise<void> } } };
  try {
    if (g.navigator?.clipboard) {
      await g.navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* sin permiso de clipboard: el link queda seleccionable a mano */
  }
  return false;
}

interface OfficialRow {
  role: MatchRole;
  name: string;
  country: string;
  licenseId: string;
}

function toRows(officials: readonly OfficialRef[]): OfficialRow[] {
  if (officials.length === 0) {
    // Arranque típico de una mesa de tres: 1er árbitro, 2do y anotador.
    return [
      { role: 'referee1', name: '', country: '', licenseId: '' },
      { role: 'referee2', name: '', country: '', licenseId: '' },
      { role: 'scorer', name: '', country: '', licenseId: '' },
    ];
  }
  return officials.map((o) => ({
    role: o.role,
    name: o.name,
    country: o.country ?? '',
    licenseId: o.licenseId ?? '',
  }));
}

export function OficialesTab(props: MesaTabProps): JSX.Element {
  const { client, matchId, sheet, identity, reload } = props;
  const t = useT();

  // --- Cuerpo arbitral -------------------------------------------------------
  const [rows, setRows] = useState<OfficialRow[]>(() => toRows(sheet.officials));
  const [savingCrew, setSavingCrew] = useState(false);
  const [crewNotice, setCrewNotice] = useState<string | null>(null);
  const [crewError, setCrewError] = useState<string | null>(null);

  const setRow = (index: number, patch: Partial<OfficialRow>): void => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const saveCrew = async (): Promise<void> => {
    const filled = rows.filter((r) => r.name.trim() !== '');
    if (filled.length !== rows.length) {
      setCrewError(t('mesa.oficiales.nameMissing'));
      return;
    }
    setSavingCrew(true);
    setCrewError(null);
    setCrewNotice(null);
    try {
      const officials: OfficialRef[] = filled.map((r) => ({
        role: r.role,
        name: r.name.trim(),
        ...(r.country.trim() !== '' ? { country: r.country.trim().toUpperCase() } : {}),
        ...(r.licenseId.trim() !== '' ? { licenseId: r.licenseId.trim() } : {}),
      }));
      const event = officialsSetEvent(officials, {
        matchId,
        seq: (await freshLastSeq(client, matchId)) + 1,
        actor: mesaActor(identity?.id ?? 'mesa'),
      });
      await client.appendEvents(matchId, [event]);
      setCrewNotice(t('mesa.oficiales.saved'));
      reload();
    } catch (err) {
      setCrewError(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setSavingCrew(false);
    }
  };

  // --- Credenciales ----------------------------------------------------------
  const [role, setRole] = useState<MatchRole>('referee1');
  const [holder, setHolder] = useState('');
  const [team, setTeam] = useState<TeamSide>('home');
  const [issuing, setIssuing] = useState(false);
  const [credError, setCredError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedCredentialResult | null>(null);
  const [copied, setCopied] = useState(false);
  /** Credencial cuyo QR se volvió a desplegar en la lista. */
  const [showQr, setShowQr] = useState<string | null>(null);

  const needsTeam = CREDENTIAL_ROLES.find((r) => r.role === role)?.needsTeam === true;

  const issue = async (): Promise<void> => {
    if (holder.trim() === '') {
      setCredError(t('mesa.oficiales.holderMissing'));
      return;
    }
    setIssuing(true);
    setCredError(null);
    setCopied(false);
    try {
      const result = await client.issueCredential(matchId, {
        role,
        holderName: holder.trim(),
        ...(needsTeam ? { team } : {}),
      });
      if (result.credentialId !== undefined && result.token !== undefined) {
        // El token en claro vive acá y en el localStorage de ESTA laptop: es lo
        // que permite firmar en la mesa sin volver a emitir (ver ActaTab).
        saveIssuedToken(matchId, result.credentialId, result.token, defaultKeyValueStore());
      }
      setIssued(result);
      setHolder('');
      reload();
    } catch (err) {
      setCredError(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setIssuing(false);
    }
  };

  const revoke = async (credential: MatchCredential): Promise<void> => {
    if (!window.confirm(t('mesa.oficiales.confirmRevoke', { holder: credential.holderName }))) {
      return;
    }
    setCredError(null);
    try {
      await client.revokeCredential(matchId, credential.subjectId);
      reload();
    } catch (err) {
      setCredError(errorMessage(err instanceof Error ? err : new Error(String(err))));
    }
  };

  // Los tokens que esta laptop emitió: con ellos el QR se puede volver a
  // mostrar (el server no los guarda en claro; ver `saveIssuedToken`).
  const storedTokens = loadIssuedTokens(matchId, defaultKeyValueStore());
  const credentialUrl = (credentialId: string, token: string): string =>
    benchCredentialUrl(portalBaseUrl(), {
      org: identity?.orgId ?? DEFAULT_ORG,
      matchId,
      credentialId,
      token,
    });

  const issuedUrl =
    issued?.credentialId !== undefined && issued.token !== undefined
      ? credentialUrl(issued.credentialId, issued.token)
      : null;

  return (
    <div>
      <Card title={t('mesa.oficiales.title')}>
        <p className="hint" style={{ marginTop: 0 }}>
          {t('mesa.oficiales.explain')}
        </p>
        {sheet.officials.length === 0 ? (
          <p className="hint">{t('mesa.oficiales.empty')}</p>
        ) : null}
        {crewError !== null ? <ErrorBox message={crewError} /> : null}
        {rows.map((row, index) => (
          <div className="official-row" key={index}>
            <label className="field">
              <span>{t('mesa.oficiales.role')}</span>
              <select
                value={row.role}
                onChange={(e) => setRow(index, { role: e.target.value as MatchRole })}
              >
                {MESA_OFFICIAL_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(roleKey(r))}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t('mesa.oficiales.name')}</span>
              <input value={row.name} onChange={(e) => setRow(index, { name: e.target.value })} />
            </label>
            <label className="field">
              <span>{t('mesa.oficiales.country')}</span>
              <input
                value={row.country}
                maxLength={3}
                onChange={(e) => setRow(index, { country: e.target.value })}
              />
            </label>
            <label className="field">
              <span>{t('mesa.oficiales.license')}</span>
              <input
                value={row.licenseId}
                onChange={(e) => setRow(index, { licenseId: e.target.value })}
              />
            </label>
            <button
              type="button"
              className="small"
              style={{ marginBottom: 8 }}
              onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
            >
              {t('mesa.oficiales.remove')}
            </button>
          </div>
        ))}
        <div className="row">
          <button
            type="button"
            className="small"
            onClick={() =>
              setRows((prev) => [...prev, { role: 'line_judge', name: '', country: '', licenseId: '' }])
            }
          >
            {t('mesa.oficiales.add')}
          </button>
          <span className="spacer" />
          {crewNotice !== null ? <span className="chip ok">{crewNotice}</span> : null}
          <button
            type="button"
            className="primary"
            disabled={savingCrew || sheet.document.closed}
            onClick={() => void saveCrew()}
          >
            {savingCrew ? t('common.saving') : t('mesa.oficiales.save')}
          </button>
        </div>
      </Card>

      <Card title={t('mesa.oficiales.credsTitle')}>
        <p className="hint" style={{ marginTop: 0 }}>
          {t('mesa.oficiales.credsExplain')}
        </p>
        {credError !== null ? <ErrorBox message={credError} /> : null}

        {issued !== null && issuedUrl !== null ? (
          <div className="cred-issued" role="status">
            <Qr
              value={issuedUrl}
              size={208}
              label={t('mesaPanel.qrLabel', { holder: issued.assignment.holderName })}
            />
            <div style={{ minWidth: 0 }}>
              <h3 style={{ marginTop: 0 }}>{t('mesa.oficiales.issuedTitle')}</h3>
              <p style={{ margin: '4px 0 10px', fontSize: '1.1rem' }}>
                <strong>{t(roleKey(issued.assignment.role))}</strong> · {issued.assignment.holderName}
                {issued.assignment.team !== undefined
                  ? ` · ${t(`mesa.team.${issued.assignment.team}` as MessageKey)}`
                  : ''}
              </p>
              <p className="hint" style={{ marginTop: 0 }}>
                {t('mesa.oficiales.qrPending')}
              </p>
              <label className="field">
                <span>{t('mesa.oficiales.issuedPin')}</span>
                <output className="pin">{issued.pin ?? ''}</output>
              </label>
              <label className="field">
                <span>{t('mesa.oficiales.issuedLink')}</span>
                <input
                  readOnly
                  className="link-copy"
                  value={issuedUrl}
                  onFocus={(e) => e.currentTarget.select()}
                />
              </label>
              <div className="row">
                <button
                  type="button"
                  onClick={() => {
                    void copyToClipboard(issuedUrl).then((ok) => setCopied(ok));
                  }}
                >
                  {copied ? t('mesa.oficiales.copied') : t('mesa.oficiales.copyLink')}
                </button>
                <span className="spacer" />
                <button type="button" className="primary" onClick={() => setIssued(null)}>
                  {t('mesa.oficiales.issuedDone')}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="official-row" style={{ gridTemplateColumns: undefined }}>
          <label className="field">
            <span>{t('mesa.oficiales.role')}</span>
            <select value={role} onChange={(e) => setRole(e.target.value as MatchRole)}>
              {CREDENTIAL_ROLES.map((r) => (
                <option key={r.role} value={r.role}>
                  {t(roleKey(r.role))}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('mesa.oficiales.holder')}</span>
            <input value={holder} onChange={(e) => setHolder(e.target.value)} />
          </label>
          {needsTeam ? (
            <label className="field" style={{ gridColumn: 'span 2' }}>
              <span>{t('mesa.oficiales.side')}</span>
              <select value={team} onChange={(e) => setTeam(e.target.value as TeamSide)}>
                <option value="home">{t('mesa.team.home')}</option>
                <option value="away">{t('mesa.team.away')}</option>
              </select>
            </label>
          ) : (
            <span style={{ gridColumn: 'span 2' }} />
          )}
          <button
            type="button"
            className="primary"
            style={{ marginBottom: 8 }}
            disabled={issuing || sheet.document.closed}
            onClick={() => void issue()}
          >
            {issuing ? '…' : t('mesa.oficiales.issue')}
          </button>
        </div>

        {sheet.assignments.length === 0 ? (
          <Empty>{t('mesa.oficiales.credsEmpty')}</Empty>
        ) : (
          <ul className="cred-list">
            {sheet.assignments.map((a) => {
              const token = storedTokens[a.subjectId];
              const url = token !== undefined ? credentialUrl(a.subjectId, token) : null;
              const open = showQr === a.subjectId && url !== null && !a.revoked;
              return (
                <li
                  key={a.subjectId}
                  className={`cred-item${a.team !== undefined ? ` ${a.team}` : ''}${
                    a.revoked ? ' revoked' : ''
                  }`}
                >
                  <div className="cred-item-head">
                    <strong>{a.holderName}</strong>
                    <span className="hint">{t(roleKey(a.role))}</span>
                    <span className="spacer" />
                    <span className="chip muted">
                      {a.kind === 'account'
                        ? t('mesa.oficiales.kindAccount')
                        : t('mesa.oficiales.kindCredential')}
                    </span>
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="hint">
                      {a.team !== undefined
                        ? `${t('mesa.oficiales.side')}: ${t(`mesa.team.${a.team}` as MessageKey)}`
                        : t('mesa.oficiales.sideNone')}
                    </span>
                    <span className="spacer" />
                    {a.revoked ? (
                      <span className="chip bad">{t('mesa.oficiales.revoked')}</span>
                    ) : null}
                    {!a.revoked && url !== null ? (
                      <button
                        type="button"
                        className="small"
                        aria-expanded={open}
                        onClick={() => setShowQr(open ? null : a.subjectId)}
                      >
                        {open ? t('mesaPanel.hideQr') : t('mesaPanel.showQr')}
                      </button>
                    ) : null}
                    {!a.revoked && a.kind === 'credential' ? (
                      <button type="button" className="small danger" onClick={() => void revoke(a)}>
                        {t('mesa.oficiales.revoke')}
                      </button>
                    ) : null}
                  </div>
                  {open && url !== null ? (
                    <div className="cred-qr">
                      <Qr
                        value={url}
                        size={168}
                        label={t('mesaPanel.qrLabel', { holder: a.holderName })}
                      />
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <p className="hint" style={{ marginTop: 0 }}>
                          {t('mesaPanel.qrAgainHint')}
                        </p>
                        <input
                          readOnly
                          className="link-copy"
                          value={url}
                          aria-label={t('mesa.oficiales.issuedLink')}
                          onFocus={(e) => e.currentTarget.select()}
                        />
                      </div>
                    </div>
                  ) : null}
                  {!a.revoked && a.kind === 'credential' && url === null ? (
                    <span className="hint">{t('mesaPanel.qrElsewhere')}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
